// What a PO line ORDERED versus what actually ARRIVED.
//
// The backend stamps `orderedQuantity` / `arrivedQuantity` / `arrivedQuantityKnown` onto
// every line of `GET /api/purchase-orders` (see purchaseOrderWorkflow.annotatePurchaseOrderLineReceipts).
// Everything staff act on after the goods land — the card list, the worksheet, the label
// print and the receive modal — must read the ARRIVED figure: a supplier that shipped 9
// against an order of 3 needs 9 labels, and a line that never turned up needs none.
//
// The fallbacks keep older cached client payloads (and optimistic post-receive updates)
// working: without the annotation a line is simply "quantity, arrival unknown".

const toQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) ? quantity : 0;
};

export const getPoLineOrderedQuantity = (item) => (
  item?.orderedQuantity === null || item?.orderedQuantity === undefined
    ? toQuantity(item?.quantity)
    : toQuantity(item.orderedQuantity)
);

export const getPoLineArrivedQuantity = (item) => (
  item?.arrivedQuantity === undefined || item?.arrivedQuantity === null
    ? toQuantity(item?.quantity)
    : toQuantity(item.arrivedQuantity)
);

/** True once the delivery has actually been counted (supplier receipt or app receipt record). */
export const isPoLineArrivalCounted = (item) => item?.arrivedQuantityKnown === true;

/** A counted line that did not arrive as ordered — the case staff need flagged on the card. */
export const poLineArrivalDiffersFromOrder = (item) => (
  isPoLineArrivalCounted(item)
    && item?.isExtraItem !== true
    && getPoLineArrivedQuantity(item) !== getPoLineOrderedQuantity(item)
);

/** A counted line that never turned up: no labels, no shelf space, nothing to weigh. */
export const poLineDidNotArrive = (item) => (
  isPoLineArrivalCounted(item) && getPoLineArrivedQuantity(item) <= 0
);

/**
 * PO lines worth labelling/shelving — drops anything counted as not delivered, and
 * zero-quantity lines with no recorded arrival (Lightspeed's partial-delivery splits
 * can leave a `0 ordered / 0 received` twin row on stale caches; before this guard
 * such a line fell through the label floor and printed one label for nothing).
 */
export const getArrivedPoLines = (items) => (
  (Array.isArray(items) ? items : []).filter((item) => {
    if (poLineDidNotArrive(item)) return false;
    return getPoLineArrivedQuantity(item) > 0 || getPoLineOrderedQuantity(item) > 0;
  })
);

/**
 * The worksheet's "Invoiced" cell: the supplier's own count, blank while nothing has
 * been counted. Printed beside Ordered so whoever works the sheet sees a mismatch
 * before they start counting rather than after.
 */
export const formatPoLineInvoicedCell = (item) => {
  if (item?.isExtraItem === true) return '—';
  if (!isPoLineArrivalCounted(item)) return '';
  return String(getPoLineArrivedQuantity(item));
};

/** The worksheet's Notes cell — why this row is not a plain "ordered N, got N". */
export const formatPoLineReceiptNote = (item) => {
  if (item?.isExtraItem === true) return 'EXTRA — not on PO';
  if (poLineDidNotArrive(item)) return 'NOT SUPPLIED';
  if (poLineArrivalDiffersFromOrder(item)) {
    const ordered = getPoLineOrderedQuantity(item);
    const arrived = getPoLineArrivedQuantity(item);
    return `${arrived > ordered ? 'OVER' : 'SHORT'} — ordered ${ordered}`;
  }
  return '';
};
